import { useEffect, useState, useRef } from 'react';
import { Page, Header, Content } from '@backstage/core-components';
import { TextField, Select, MenuItem, Button } from '@material-ui/core';
import {
  useApi,
  fetchApiRef,
  discoveryApiRef,
} from '@backstage/core-plugin-api';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { KONE_ROOT_VARS } from './details/koneColors';

type Filters = {
  lifecycle: string;
  securityImpact: string;
  businessUnit: string;
  department: string;
  ban: string;
  name: string;
};

type FilterOptions = {
  lifecycle: string[];
  // application_data.security_impact is SMALLINT, so the API returns numbers
  // here — the dropdown values are coerced to strings for the <Select>.
  securityImpact: (string | number)[];
  businessUnit: string[];
  department: string[];
};

// Reuses the same KONE Flow Design System tokens as View 2 (see
// ./details/koneColors.ts's docstring) — this page previously defined its
// own parallel `--brand-*` variable set for the identical colors, which is
// exactly the kind of token duplication/drift that file exists to prevent.
const STYLES = `
:root {
${KONE_ROOT_VARS}
}

  .page-shell {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  .filters-panel {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    align-items: flex-end;
    padding: 16px;
    border: 1px solid var(--kone-border);
    border-radius: 12px;
    background: var(--kone-surface);
  }

  .filter-field {
    min-width: 180px;
    flex: 1 1 180px;
  }

  .filter-actions {
    margin-left: auto;
    display: flex;
    align-items: center;
  }

  .features-grid {
    width: 100%;
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 16px;
    padding: 4px 0 0;
  }

  .feature-card {
    border: 1px solid var(--kone-border);
    border-radius: 12px;
    background: var(--kone-surface);
    min-height: 240px;
    overflow: hidden;
    cursor: pointer;
    transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;
    box-shadow: 0 4px 12px rgba(20, 80, 245, 0.04);
  }

  .feature-card:hover {
    transform: translateY(-2px);
    border-color: var(--kone-blue);
    box-shadow: 0 8px 20px rgba(20, 80, 245, 0.1);
  }

  .feature-card-content {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    height: 100%;
    padding: 16px;
    background: linear-gradient(135deg, var(--kone-sand) 0%, var(--kone-surface) 100%);
  }

  .feature-card .title {
    margin: 0 0 4px;
    font-size: 18px;
    font-weight: 600;
    color: var(--kone-blue);
  }

  .feature-card .description {
    margin: 0;
    font-size: 14px;
    line-height: 1.5;
    color: var(--kone-text-muted);
  }

  .feature-card .meta-chip {
    display: inline-flex;
    align-items: center;
    width: fit-content;
    padding: 4px 8px;
    margin-top: 8px;
    border-radius: 999px;
    background: var(--kone-light-blue);
    color: var(--kone-text);
    font-size: 12px;
  }

  .pagination-controls {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 12px;
    margin-top: 24px;
  }

  .empty-state {
    margin: 0;
    text-align: center;
    padding: 48px 16px;
    border: 1px dashed var(--kone-border);
    border-radius: 12px;
    color: var(--kone-text-muted);
    background: var(--kone-surface);
  }

  /* Clear Filters button — KONE Blue when clickable, neutral when disabled
     (no filters set yet), matching MUI's disabled-state affordance without
     falling back to Material UI's own default purple/gray palette. */
  .clear-filters-btn {
    background-color: var(--kone-blue) !important;
    color: var(--kone-white) !important;
  }

  .clear-filters-btn:hover {
    background-color: var(--kone-blue) !important;
    opacity: 0.92;
  }

  .clear-filters-btn.Mui-disabled {
    background-color: var(--kone-neutral-5) !important;
    color: var(--kone-neutral-3) !important;
  }

  @media (max-width: 1400px) {
    .features-grid {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
  }

  @media (max-width: 768px) {
    .features-grid {
      grid-template-columns: 1fr;
    }

    .filters-panel {
      flex-direction: column;
      align-items: stretch;
    }

    .filter-actions {
      margin-left: 0;
    }
  }
`;

// Query-param keys used to persist filters/pagination in the URL, so the
// selection survives navigating into an application and back (via the
// "Back to Business Applications" link, or the browser back button)
// instead of resetting every time this page remounts.
const FILTER_PARAM_KEYS: Record<keyof Filters, string> = {
  ban: 'ban',
  name: 'name',
  lifecycle: 'lifecycle',
  securityImpact: 'securityImpact',
  businessUnit: 'businessUnit',
  department: 'department',
};
const PAGE_PARAM_KEY = 'page';

const filtersFromSearchParams = (params: URLSearchParams): Filters => ({
  ban: params.get(FILTER_PARAM_KEYS.ban) ?? '',
  name: params.get(FILTER_PARAM_KEYS.name) ?? '',
  lifecycle: params.get(FILTER_PARAM_KEYS.lifecycle) ?? '',
  securityImpact: params.get(FILTER_PARAM_KEYS.securityImpact) ?? '',
  businessUnit: params.get(FILTER_PARAM_KEYS.businessUnit) ?? '',
  department: params.get(FILTER_PARAM_KEYS.department) ?? '',
});

const pageFromSearchParams = (params: URLSearchParams): number => {
  const raw = Number.parseInt(params.get(PAGE_PARAM_KEY) ?? '0', 10);
  return Number.isFinite(raw) && raw > 0 ? raw : 0;
};

// The filter API reports applications with no security impact level yet as 0
// (list_filters COALESCEs NULL to 0, and list_apps maps a 0 filter back to
// "security_impact IS NULL"). The values stay "0".."4" on the wire and in the
// URL; only their labels change. Any other value is shown as-is.
const SECURITY_IMPACT_LABELS: Record<string, string> = {
  '0': 'Not assessed',
  '1': '1 - Minor',
  '2': '2 - Medium',
  '3': '3 - Major',
  '4': '4 - Critical',
};

const securityImpactLabel = (value: string): string =>
  SECURITY_IMPACT_LABELS[value] ?? value;

export const BusinessApplicationsPage = () => {
  const fetchApi = useApi(fetchApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  const navigate = useNavigate();
  const featuresRef = useRef<HTMLDivElement>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const [filters, setFilters] = useState<Filters>(() =>
    filtersFromSearchParams(searchParams),
  );

  const clearFilters = () => {
    setFilters({
      ban: '',
      name: '',
      lifecycle: '',
      securityImpact: '',
      businessUnit: '',
      department: '',
    });
  };
  const isClearDisabled = Object.values(filters).every(v => !v);

  const [filterOptions, setFilterOptions] = useState<FilterOptions>({
    lifecycle: [],
    securityImpact: [],
    businessUnit: [],
    department: [],
  });

  const [applications, setApplications] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(() => pageFromSearchParams(searchParams));
  const [debouncedBan, setDebouncedBan] = useState(filters.ban);
  const [debouncedName, setDebouncedName] = useState(filters.name);
  const applicationsRequestRef = useRef(0);
  const limit = 6;

  useEffect(() => {
    applicationsRequestRef.current += 1;
    const timeoutId = window.setTimeout(() => {
      setDebouncedBan(filters.ban);
      setDebouncedName(filters.name);
    }, 500);
    return () => window.clearTimeout(timeoutId);
  }, [filters.ban, filters.name]);

  // Keep the URL query string in sync with the current filters/page so the
  // selection is preserved across navigation (e.g. opening an application
  // and using "Back to Business Applications" to return here).
  useEffect(() => {
    const next = new URLSearchParams();
    (Object.keys(FILTER_PARAM_KEYS) as (keyof Filters)[]).forEach(key => {
      const value = filters[key];
      if (value) next.set(FILTER_PARAM_KEYS[key], value);
    });
    if (page > 0) next.set(PAGE_PARAM_KEY, String(page));
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, page]);

  // FETCH FILTERS (ON MOUNT, AND AGAIN WHENEVER BUSINESS UNIT CHANGES)
  //
  // The department list returned by GET /applications/filters is scoped to
  // the currently selected business unit (via the businessUnit query param)
  // so the Department dropdown only ever shows departments that actually
  // belong to it, instead of every department across every business unit.
  // lifecycle/securityImpact/businessUnit stay unscoped on every call.
  useEffect(() => {
    const fetchFilters = async () => {
      try {
        const baseUrl = await discoveryApi.getBaseUrl('business-applications');

        const params = filters.businessUnit
          ? `?${new URLSearchParams({ businessUnit: filters.businessUnit })}`
          : '';
        const res = await fetchApi.fetch(
          `${baseUrl}/applications/filters${params}`,
        );

        const data = await res.json();
        setFilterOptions(data);
      } catch {
        // Filter options remain empty if the endpoint is unavailable.
      }
    };

    fetchFilters();
  }, [fetchApi, discoveryApi, filters.businessUnit]);

  // A previously selected department may not belong to a newly selected
  // business unit — clear it so the UI never holds an invalid combination
  // while the scoped department list above is (re)loading. Skipped on the
  // very first run so a deep-linked URL (?businessUnit=X&department=Y)
  // isn't immediately wiped on mount.
  const isFirstBusinessUnitRender = useRef(true);
  useEffect(() => {
    if (isFirstBusinessUnitRender.current) {
      isFirstBusinessUnitRender.current = false;
      return;
    }
    setFilters(prev => (prev.department ? { ...prev, department: '' } : prev));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.businessUnit]);

  // FETCH APPLICATIONS (ON FILTER / PAGE CHANGE)
  useEffect(() => {
    if (filters.ban !== debouncedBan || filters.name !== debouncedName) {
      return () => {};
    }

    let cancelled = false;
    const requestId = ++applicationsRequestRef.current;
    const fetchApplications = async () => {
      try {
        const params = new URLSearchParams({
          limit: String(limit),
          offset: String(page * limit),
          ban: debouncedBan,
          name: debouncedName,
          lifecycle: filters.lifecycle,
          securityImpact: filters.securityImpact,
          businessUnit: filters.businessUnit,
          department: filters.department,
        });

        const baseUrl = await discoveryApi.getBaseUrl('business-applications');

        const res = await fetchApi.fetch(`${baseUrl}/applications?${params}`);

        const data = await res.json();
        if (cancelled || requestId !== applicationsRequestRef.current) return;
        setApplications(data.data || []);
        setTotal(data.total ?? 0);
      } catch {
        // Keep the existing application results when a refresh fails.
      }
    };

    void fetchApplications();
    return () => {
      cancelled = true;
    };
  }, [
    fetchApi,
    discoveryApi,
    debouncedBan,
    debouncedName,
    filters.ban,
    filters.name,
    filters.lifecycle,
    filters.securityImpact,
    filters.businessUnit,
    filters.department,
    page,
  ]);

  // MOUSE TRACKING FOR GLOW EFFECT
  useEffect(() => {
    const handleMouseMove = (ev: PointerEvent) => {
      if (!featuresRef.current) return;

      const cardElements =
        featuresRef.current.querySelectorAll('.feature-card');
      cardElements.forEach(cardEl => {
        const rect = (cardEl as HTMLElement).getBoundingClientRect();
        (cardEl as HTMLElement).style.setProperty(
          '--x',
          String(ev.clientX - rect.left),
        );
        (cardEl as HTMLElement).style.setProperty(
          '--y',
          String(ev.clientY - rect.top),
        );
      });
    };

    document.addEventListener('pointermove', handleMouseMove);
    return () => document.removeEventListener('pointermove', handleMouseMove);
  }, []);

  return (
    <Page themeId="tool">
      <style>{STYLES}</style>

      <Header title="Business Applications" />
      <Content>
        <div className="page-shell">
          <div className="filters-panel">
            <div className="filter-field">
              <TextField
                label="Business Application Number"
                fullWidth
                value={filters.ban}
                onChange={e => {
                  setFilters({ ...filters, ban: e.target.value });
                  setPage(0);
                }}
              />
            </div>

            <div className="filter-field">
              <TextField
                label="Name"
                fullWidth
                value={filters.name}
                onChange={e => {
                  setFilters({ ...filters, name: e.target.value });
                  setPage(0);
                }}
              />
            </div>

            <div className="filter-field">
              <Select
                value={filters.lifecycle}
                onChange={e => {
                  setFilters({
                    ...filters,
                    lifecycle: e.target.value as string,
                  });
                  setPage(0);
                }}
                displayEmpty
                fullWidth
                renderValue={selected => (selected as string) || 'Lifecycle'}
              >
                <MenuItem value="">Lifecycle</MenuItem>
                {(filterOptions.lifecycle || []).map(
                  (value: string, i: number) => (
                    <MenuItem key={i} value={value}>
                      {value}
                    </MenuItem>
                  ),
                )}
              </Select>
            </div>

            <div className="filter-field">
              <Select
                value={filters.securityImpact}
                onChange={e => {
                  setFilters({
                    ...filters,
                    securityImpact: e.target.value as string,
                  });
                  setPage(0);
                }}
                displayEmpty
                fullWidth
                renderValue={selected =>
                  securityImpactLabel(selected as string) || 'Security Impact'
                }
              >
                <MenuItem value="">Security Impact</MenuItem>
                {(filterOptions.securityImpact || [])
                  .map(value => Number(value))
                  .sort((a: number, b: number) => a - b)
                  .map((value: number, i: number) => (
                    <MenuItem key={i} value={String(value)}>
                      {securityImpactLabel(String(value))}
                    </MenuItem>
                  ))}
              </Select>
            </div>

            <div className="filter-field">
              <Select
                value={filters.businessUnit}
                onChange={e => {
                  setFilters({
                    ...filters,
                    businessUnit: e.target.value as string,
                  });
                  setPage(0);
                }}
                displayEmpty
                fullWidth
                renderValue={selected =>
                  (selected as string) || 'Business Unit'
                }
              >
                <MenuItem value="">Business Unit</MenuItem>
                {(filterOptions.businessUnit || []).map(
                  (value: string, i: number) => (
                    <MenuItem key={i} value={value}>
                      {value}
                    </MenuItem>
                  ),
                )}
              </Select>
            </div>

            <div className="filter-field">
              <Select
                value={filters.department}
                onChange={e => {
                  setFilters({
                    ...filters,
                    department: e.target.value as string,
                  });
                  setPage(0);
                }}
                displayEmpty
                fullWidth
                renderValue={selected => (selected as string) || 'Department'}
              >
                <MenuItem value="">Department</MenuItem>
                {(filterOptions.department || []).map(
                  (value: string, i: number) => (
                    <MenuItem key={i} value={value}>
                      {value}
                    </MenuItem>
                  ),
                )}
              </Select>
            </div>

            <div className="filter-actions">
              <Button
                variant="contained"
                className="clear-filters-btn"
                disabled={isClearDisabled}
                onClick={() => {
                  clearFilters();
                  setPage(0);
                }}
              >
                Clear Filters
              </Button>
            </div>
          </div>

          {applications.length === 0 ? (
            <p className="empty-state">No applications found.</p>
          ) : (
            <>
              <div className="features-grid" ref={featuresRef}>
                {applications.map((application: any, index: number) => {
                  const query = searchParams.toString();
                  const detailsPath = `/business-applications/${
                    application.number
                  }${query ? `?${query}` : ''}`;
                  return (
                    <div
                      key={index}
                      className="feature-card"
                      onClick={() => navigate(detailsPath)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={event => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          navigate(detailsPath);
                        }
                      }}
                    >
                      <div className="feature-card-content">
                        <div>
                          <p className="title">
                            {application.name ?? 'Unnamed Application'}
                          </p>
                          <p className="description">
                            <strong>{application.number ?? 'No ID'}</strong>
                            <br />
                            {application.department ?? 'Department: N/A'}
                            {application.lifecycle
                              ? ` • ${application.lifecycle}`
                              : ''}
                          </p>
                        </div>
                        <div className="meta-chip">View details</div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="pagination-controls">
                <Button
                  variant="contained"
                  disabled={page === 0}
                  onClick={() => setPage(page - 1)}
                >
                  Previous
                </Button>

                <div
                  style={{
                    minWidth: '120px',
                    textAlign: 'center',
                    color: 'var(--kone-text-muted)',
                  }}
                >
                  Page {page + 1} ({total} results)
                </div>

                <Button
                  variant="contained"
                  disabled={(page + 1) * limit >= total}
                  onClick={() => setPage(page + 1)}
                >
                  Next
                </Button>
              </div>
            </>
          )}
        </div>
      </Content>
    </Page>
  );
};
